import React, { useCallback, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ProductDetail from '@univdiam/ui/customComponents/CatalogProduct/ProductDetail'
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '@/http/api';
import { useAuthStore } from '@/store';
import { useSocketStore } from '@/socketStore';
import { useGroup } from '@/sharedData/group';
import Drawer from '@univdiam/ui/customComponents/Drawer';
import AddProduct3 from '@univdiam/ui/customComponents/forms/AddProduct3';
import { shareUrl } from '@univdiam/ui/lib/shareUrl'
import TicketIcon from '@/assets/FeaturedIcon.svg';
import { toast } from 'sonner';
type ProductImage = {
  src: string;
  alt: string;
  thumbnail?: string;
}
type BOMType = {
  "metal"?: string,
  "diamond"?: string,
  "price"?: string,
  "id": string,
  "size": string,
  "group": string,
  "shape": string,
  "ptr_wt": number,
  "subGroup": string,
  "subSubGroup": string,
  "no_of_stones": number,
  "total_weight": number
}

type CADType = {
  order_id?: string
  bom: string;
  attachments: string;
  selling_price: string;
  cost_price: string
  instruction: string
  notes?: string
  iFrameLink: string
  customer_id?: string
  order_ref?: string
  customFields?: { id: string; label: string; value: string }[]
}

type CADOrderType = {
  order_id?: string
  bom: string;
  price: string;
  instructions: string;
  cad_design_id: string;
  productId: string
}

const addCadOrderRequest = ({ order_id, bom, price, instructions, cad_design_id, productId }: CADOrderType) => {
  return api.post(`/cad_order_req/create`, {
    order_id, price, bom, instructions, cad_design_id, productId
  });
}

const getOrderCadSubmiited = (order_id: string) => {
  return api.get(`/cad_order_req/${order_id}`);
}

const addCad = ({ order_id, attachments, bom, instruction,
  iFrameLink, customer_id, order_ref, notes, customFields }: CADType) => {
  return api.post(`/cad/create`, {
    order_id, attachments, bom, instruction,
    iFrameLink, customer_id, order_ref, notes, customFields
  });
}

const Product = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { id: messageId } = useParams()
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const order_id = searchParams.get("order_id");
  const order_ref = searchParams.get("order_ref");
  const customer_id = searchParams.get("customer_id");
  const [filterdValue, setFilterdValue] = useState({})
  const location = useLocation();
  let currentCAD = location.state.metadata

  const sendNotification = useSocketStore((s) => s.sendNotification);

  if (from === 'CAD') {
    currentCAD = location.state
  }

  const productImages: ProductImage[] = from === 'CAD' ? currentCAD.attachments : currentCAD.attachments.map((a: string, i: number) => (
    {
      src: a,
      alt: `Product Image ${i + 1}`,
      thumbnail: a
    }
  ))


  const BOM = currentCAD?.bom?.flatMap(item =>
    item?.combination
      ? [
        {
          id: item.id,
          price: item.price,
          ...item.combination // dynamically spread all combination fields
        }
      ]
      : []
  ) as BOMType[];

  const { mutate, isPending } = useMutation({
    mutationFn: (values: CADType) => {
      // console.log('values', values)
      return addCad({ ...values, order_id: order_id!, customer_id: customer_id!, order_ref: order_ref! })
    },
    onSuccess: async () => {
      navigate(-1)
      sendNotification({
        action_by: user?.role!,
        action_by_id: user?.id.toString()!,
        action_type: "cad_added",
        order_id: order_id!,
        title: `New CAD added`,
        metadata: {
          order_ref: order_id!,
          order_id: order_ref!, // #UD type string
          customer_id: customer_id!,
          adminName: user?.full_name!,
        },
      });
    }
  })

  const { mutate: createCADOrder } = useMutation({
    mutationFn: (values: CADOrderType) => {
      return addCadOrderRequest({ ...values, order_id: order_id!, productId: currentCAD?.fileName })
    },
    onSuccess: () => {
      toast.success('Order request submitted', { position: 'top-center' })
      navigate(-1)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to submit order request')
    }
  })

  const { data: orderCadSubmitted } = useQuery({
    queryKey: ["order-cad-submitted", order_id],
    queryFn: () => getOrderCadSubmiited(order_id!),
    enabled: !!order_id,
  })
  const submittedOrders = orderCadSubmitted?.data?.cad_orders

  const { mutateAsync: savePricingMutation } = useMutation({
    mutationFn: (bom: any[]) =>
      api.patch(`/admin/discussion/${messageId}/bom`, { bom })
  })

  const onSavePricing = useCallback(async (bom: any[]) => {
    await savePricingMutation(bom)

    // Build a price map from the flat edited BOM
    const priceMap: Record<string, any> = {}
    bom.forEach((item: any) => { priceMap[item.id] = item.price })

    // Update the nested bom in metadata and replace navigation state
    // so a page refresh reflects the new prices without re-navigating
    const currentMetadata = location.state.metadata
    const updatedBom = (currentMetadata.bom || []).map((item: any) => ({
      ...item,
      price: priceMap[item.id] !== undefined ? priceMap[item.id] : item.price
    }))

    navigate(location.pathname + location.search, {
      replace: true,
      state: {
        ...location.state,
        metadata: { ...currentMetadata, bom: updatedBom }
      }
    })
  }, [savePricingMutation, location, navigate])

  // --- Full product edit (discussion view) ---
  const [editOpen, setEditOpen] = useState(false)
  const { data: groupResponse } = useGroup()
  const groupData = groupResponse?.data?.productGroups

  const [isMediaUploading, setIsMediaUploading] = useState(false)
  const handleSendToUpload = async (files: File[]): Promise<string[]> => {
    setIsMediaUploading(true)
    try {
      const formData = new FormData()
      files.forEach((file) => formData.append('files', file))
      const response = await api.post(`/upload?customerId=${user?.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data?.files ?? []
    } catch (error) {
      toast.error(`Error occured while uploading media : ${error}`)
      return []
    } finally {
      setIsMediaUploading(false)
    }
  }

  const { mutateAsync: updateProduct, isPending: isUpdatingProduct } = useMutation({
    // Overwrites the discussion message's full product metadata.
    // Backend contract: PATCH /admin/discussion/:messageId { metadata: { bom, attachments, instruction, iframe, customFields } }
    mutationFn: (metadata: Record<string, any>) =>
      api.patch(`/admin/discussion/${messageId}`, { metadata }),
  })

  const handleUpdateProduct = async (values: any) => {
    if (!values?.metadata) return
    const parsed = JSON.parse(values.metadata)
    try {
      await updateProduct(parsed)
      // Refresh nav state so the view + subsequent "Send to customer" use edited data.
      const currentMetadata = location.state.metadata
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...location.state, metadata: { ...currentMetadata, ...parsed } },
      })
      setEditOpen(false)
      toast.success('Product updated', { position: 'top-center' })
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update product')
    }
  }

  const uniqueMetals = [...new Set(currentCAD.bom.map((item: BOMType) => item.metal))] as string[];

  const uniqueDiamonds = [...new Set(currentCAD.bom.map((item: BOMType) => item.diamond))] as string[];

  const filters = {
    diamond_quality: uniqueDiamonds,
    metal_type: uniqueMetals
  }
  const handleBack = () => {
    navigate(-1)
  }

  const generateShareLink = useCallback(async (params: { cad_design_id: string | number, share_data: Record<string, any> }) => {
    try {
      const res = await api.post('/cad/share', params)
      const shareCode = res?.data?.shareCode ?? res?.data?.token
      if (!shareCode) return null
      // Admin has no retailer identity — `user` here is the staff account, which has
      // no business_name — so the link falls back to the neutral 'business' slug.
      return shareUrl('share_product', shareCode)
    } catch (err) {
      console.error('Failed to generate share link:', err)
      return null
    }
  }, [])

  return (
    <div className='p-[16px]'>
      <ProductDetail filters={filters} productImages={productImages} from={from!} BOM={BOM} addCad={mutate} filterdValue={filterdValue} setFilterdValue={setFilterdValue} currentCAD={currentCAD} isPending={isPending} isAdmin={true} handleBack={handleBack} generateShareLink={generateShareLink} createCADOrder={createCADOrder} submittedOrders={submittedOrders} onSavePricing={from === 'discussion' ? onSavePricing : undefined} onEditProduct={from === 'discussion' ? () => setEditOpen(true) : undefined} />

      {from === 'discussion' && editOpen &&
        <Drawer
          open={editOpen}
          setOpen={setEditOpen}
          contentClassName="lg:max-w-[900px]"
          title={
            <p className='flex items-center gap-[8px]'>
              <img src={TicketIcon} alt="Edit product" />
              Edit product
            </p>
          }
        >
          <div className='w-full pb-4'>
            <AddProduct3
              handleSendToUpload={handleSendToUpload}
              mediaLoading={isMediaUploading}
              handleSendDiscussion={handleUpdateProduct}
              isDicussionPending={isUpdatingProduct}
              isDiscussionLoading={false}
              groupData={groupData || []}
              submitLabel="Save changes"
              initialData={{
                bom: currentCAD?.bom,
                attachments: currentCAD?.attachments,
                iframe: currentCAD?.iframe ?? currentCAD?.iframe_link,
                instruction: currentCAD?.instruction,
                customFields: currentCAD?.customFields,
              }}
            />
          </div>
        </Drawer>
      }
    </div>
  )
}

export default Product
